import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import type { Category as CurriculumCategory, Curriculum, Goal } from '@ilteducation/curriculum-types';
import type { VideoV1 } from '@ilteducation/content-registry-types';
import { Category } from '@ilteducation/content-ref-data-types';
import store from '@/store';
import {
  createResourceGoal,
  deleteResourceGoal,
  getAllCurriculums,
  getAllCurriculumsGoals,
} from '@/data-provider/curriculum';
import admin from '@/plugins/admin';
import IdIcon from '@/components/IdIcon.vue';
import { getAllRefData } from '@/data-provider/refDataProvider';

type SortedGoal = {
  type: 'grade' | 'category' | 'goal';
  grade?: string;
  category?: string;
  goal?: Goal;
  disabled: boolean;
};

type VideoResource = {
  resourceId: string;
  market: string;
  subjectId: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'VideoCurriculums',
  props: {
    item: null
  },
  setup(__props: any) {

const props = __props as { item: VideoV1 }



const availableGoals = ref<Goal[]>([]);
const allCurriculums = ref<Curriculum[]>([]);
const selectedGoalSubject = ref<string>();
const selectedGoal = ref<SortedGoal>();
const selectedVideoResourceId = ref<string>();
const goalListRef = ref();
const availableSubjects = ref<Category[]>([]);
getAllRefData('categories').then((categories) => {
  availableSubjects.value = categories as Category[];
});

const curriculumMarkets = ['se', 'fi', 'nob'];

const videoResources = computed<VideoResource[]>(() => {
  const byResourceId = new Map<string, VideoResource>();
  (props.item.videos ?? []).forEach(({ market, categoryId }) => {
    if (!market || !categoryId) {
      return;
    }
    const resourceId = `${props.item.id}-${market}-${categoryId}`;
    byResourceId.set(resourceId, { resourceId, market, subjectId: categoryId });
  });
  return [...byResourceId.values()];
});
const videoResourceIds = computed(() => videoResources.value.map((resource) => resource.resourceId));
const videoMarkets = computed(() => new Set(videoResources.value.map((resource) => resource.market)));

const videoSubjectOptions = computed(() =>
  videoResources.value
    .filter(
      (resource) =>
        allCurriculums.value.find((c) => c.id === selectedGoal.value?.goal?.curriculumId)?.country === resource.market,
    )
    .map((resource) => ({
      text: availableSubjects.value.find((s) => s.id === resource.subjectId)?.category ?? resource.subjectId,
      value: resource.resourceId,
    })),
);

watch(videoSubjectOptions, (options) => {
  if (selectedVideoResourceId.value && !options.some((option) => option.value === selectedVideoResourceId.value)) {
    selectedVideoResourceId.value = undefined;
  }
});

const combinedCategory = (category?: CurriculumCategory, res?: CurriculumCategory): CurriculumCategory | undefined => {
  if (!category) {
    return undefined;
  }
  const mergeTitles = (
    title1: CurriculumCategory['title'],
    title2: CurriculumCategory['title'],
  ): CurriculumCategory['title'] => {
    const combinedTitle: CurriculumCategory['title'] = {};
    Object.keys(title1).forEach((language) => {
      combinedTitle[language] = `${title1[language]} | ${title2[language]}`;
    });
    return combinedTitle;
  };
  const title = res ? mergeTitles(res.title, category.title) : category.title;
  const currentCategory: CurriculumCategory = { title };
  if (category && category.child) {
    return combinedCategory(category.child, currentCategory);
  }
  return currentCategory;
};
getAllCurriculums()
  .then((curriculums) => {
    allCurriculums.value = curriculums;
    return curriculums.filter((c) => curriculumMarkets.includes(c.defaultLanguage));
  })
  .then((currs) => (currs ? Promise.all(currs.map((c) => getAllCurriculumsGoals(c.id))) : Promise.resolve([])))
  .then((goals) => {
    availableGoals.value = goals.flat();
  });

const selectableGoals = computed(() =>
  availableGoals.value.filter((goal) => {
    const country = allCurriculums.value.find((c) => c.id === goal.curriculumId)?.country;
    return country !== undefined && videoMarkets.value.has(country);
  }),
);

const availableGoalSubjects = computed(() => {
  const subjs = selectableGoals.value
    .map((goal) => curriculumMarkets.map((lang) => goal.subject?.[lang]))
    .flat()
    .filter((subject): subject is string => Boolean(subject));
  subjs.sort();
  return Array.from(new Set(subjs));
});

const getCurriculumGoalText = (value?: { [market: string]: string }): string => {
  if (!value) {
    return '';
  }
  return (Object.entries(value).find(([market, _subject]) => curriculumMarkets.includes(market))?.[1] ??
    Object.values(value)?.[0]) as string;
};

const filteredGoals = computed(() =>
  selectableGoals.value
    .filter((goal) => getCurriculumGoalText(goal.subject) === selectedGoalSubject.value)
    .map((goal) => ({
      ...goal,
      category: combinedCategory(goal.category),
    })),
);

const goalOptions = computed(() => {
  const copy = [...filteredGoals.value];
  copy.sort((a, b) => {
    if (a.grade === b.grade) {
      const aCat = getCurriculumGoalText(a.category?.title) || 'No category';
      const bCat = getCurriculumGoalText(b.category?.title) || 'No category';
      if (aCat === bCat) {
        return getCurriculumGoalText(a.content).localeCompare(getCurriculumGoalText(b.content), 'sv');
      }
      return aCat.localeCompare(bCat, 'sv');
    }
    return a.grade.localeCompare(b.grade, 'sv');
  });
  const res: Array<SortedGoal> = [];
  let currentGrade = '';
  let currentCategory = '';
  copy.forEach((goal) => {
    if (goal.grade !== currentGrade) {
      currentGrade = goal.grade;
      res.push({
        type: 'grade',
        grade: currentGrade,
        disabled: true,
      });
    }
    if (getCurriculumGoalText(goal.category?.title) !== currentCategory) {
      currentCategory = getCurriculumGoalText(goal.category?.title) || '';
      if (currentCategory) {
        res.push({
          type: 'category',
          category: currentCategory,
          disabled: true,
        });
      }
    }
    res.push({
      type: 'goal',
      goal,
      disabled: false,
    });
  });
  return res;
});

const onAddGoal = async () => {
  if (!selectedGoal.value || !selectedGoal.value.goal || !selectedVideoResourceId.value) {
    return;
  }
  await createResourceGoal(selectedVideoResourceId.value, selectedGoal.value.goal.id);
  goalListRef.value.fetchData();
  store.dispatch('snackbar/show', {
    message: 'Goal added',
    color: 'success',
  });
};

const onDeleteGoal = async (goal: Goal, resourceId: string) => {
  if (
    await admin.confirm(
      'Delete curriculum goal',
      `Are you sure you want to delete this goal: '${getCurriculumGoalText(goal.content)}'?`,
    )
  ) {
    await deleteResourceGoal(resourceId, goal.id);
    goalListRef.value.fetchData();
    store.dispatch('snackbar/show', {
      message: 'Goal deleted',
      color: 'success',
    });
  }
};

const curriculumGoalFields = computed(() => [
  {
    label: 'Curriculum subject',
    source: `goal.subject`,
  },
  'goal.market',
  {
    source: 'goal.grade',
    sortable: true,
  },
  {
    label: 'Category',
    source: `goal.category.title`,
    sortable: true,
  },
  {
    label: 'Content',
    source: `goal.content`,
    sortable: true,
  },
]);

const getMarketFromGoal = (goal: Goal) => allCurriculums.value.find((c) => c.id === goal.curriculumId)?.country ?? '';

return { __sfc: true,props, availableGoals, allCurriculums, selectedGoalSubject, selectedGoal, selectedVideoResourceId, goalListRef, availableSubjects, curriculumMarkets, videoResources, videoResourceIds, videoMarkets, videoSubjectOptions, combinedCategory, selectableGoals, availableGoalSubjects, getCurriculumGoalText, filteredGoals, goalOptions, onAddGoal, onDeleteGoal, curriculumGoalFields, getMarketFromGoal, IdIcon }
}

})